import { cn } from '@/lib/utils';

interface BetaBadgeProps {
  /** Optional size variant. `sm` matches the dashboard's typical
   *  inline metadata pills (10-11px text); `xs` is even more compact
   *  for tight contexts like tab strips. */
  size?: 'xs' | 'sm';
  className?: string;
}

/**
 * BetaBadge — TELV1-296 UX follow-up.
 *
 * Small "Beta" pill used to signal that a feature is still in active
 * development and may change. Currently surfaced on the Research
 * Assistant tab link and its in-page context bar.
 *
 * Design notes:
 *   - Uses the brand `primary` palette at low opacity so the badge
 *     reads as "ours" rather than an external annotation — but the
 *     border + bold uppercase text still mark it visually distinct
 *     from regular content.
 *   - `font-semibold` + `uppercase` + `tracking-wide` keeps the four
 *     letters legible at 10px without bumping the visual weight of
 *     surrounding labels.
 *   - `shrink-0` so the pill never compresses inside flex containers
 *     when sibling content gets long (e.g. a long project title).
 */
function BetaBadge({ size = 'sm', className }: BetaBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center justify-center rounded-full border font-semibold uppercase tracking-wide shrink-0',
        'bg-primary/10 border-primary/20 text-primary',
        size === 'xs' && 'text-[9px] px-1.5 py-0 h-4 leading-none',
        size === 'sm' && 'text-[10px] px-1.5 py-0.5 leading-none',
        className,
      )}
      aria-label="Beta feature"
    >
      Beta
    </span>
  );
}

export default BetaBadge;
